import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { useAuthStore } from '../stores/authStore';
import PricingConfig from '../components/PricingConfig';

interface UserInfo {
  userId: string;
  email: string;
  name: string;
  emailVerified: boolean;
  role: string;
  createdAt: string;
  updatedAt: string;
}

interface PlatformClick {
  platform: string;
  createdAt: string;
  userId: string;
  userName: string;
  userEmail: string;
}

interface FeedbackItem {
  comment: string;
  rating: number;
  platform: string;
  userEmail: string;
  createdAt: string;
}

const ADMIN_EMAILS = ['giovanny.pinilla@yahoo.com.co', 'giovannyepinillaa@gmail.com', 'luishsd1978@gmail.com'];

// Track platform click - sends to server
export async function trackPlatformClick(platform: string, _userId: string, userName: string) {
  try {
    const authData = JSON.parse(localStorage.getItem('fuigo-auth') || '{}');
    const token = authData?.state?.token;
    if (!token) return;
    fetch(`${API_BASE_URL}/admin/track-click`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform, userName }),
    }).catch(() => {}); // fire and forget
  } catch { /* ignore */ }
}

export default function AdminDashboard() {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clicks, setClicks] = useState<PlatformClick[]>([]);
  const [platformStats, setPlatformStats] = useState<Record<string, number>>({});
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const { token, user } = useAuthStore();

  const isAdmin = user?.role === 'admin' || ADMIN_EMAILS.includes(user?.email || '');

  const fetchData = useCallback(async () => {
    if (!token) {
      setError('No hay token de autenticación. Cierra sesión e inicia de nuevo.');
      return;
    }
    setLoading(true);
    setError('');
    const hdrs = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const errors: string[] = [];
    try {
      // Fetch users
      try {
        const usersRes = await fetch(`${API_BASE_URL}/admin/users`, { headers: hdrs });
        if (usersRes.ok) {
          const data = await usersRes.json();
          setUsers(data.users || []);
        } else {
          const errData = await usersRes.json().catch(() => ({}));
          errors.push(`Usuarios: ${errData.message || usersRes.status}`);
        }
      } catch (e) { errors.push(`Usuarios: Error de conexión`); }

      // Fetch clicks
      try {
        const clicksRes = await fetch(`${API_BASE_URL}/admin/clicks`, { headers: hdrs });
        if (clicksRes.ok) {
          const data = await clicksRes.json();
          setClicks(data.clicks || []);
          setPlatformStats(data.platformStats || {});
        } else {
          const errData = await clicksRes.json().catch(() => ({}));
          errors.push(`Clicks: ${errData.message || clicksRes.status}`);
        }
      } catch (e) { errors.push(`Clicks: Error de conexión`); }

      // Fetch feedback
      try {
        const feedbackRes = await fetch(`${API_BASE_URL}/admin/feedback`, { headers: hdrs });
        if (feedbackRes.ok) {
          const data = await feedbackRes.json();
          setFeedback(data.feedback || []);
        } else {
          const errData = await feedbackRes.json().catch(() => ({}));
          errors.push(`Feedback: ${errData.message || feedbackRes.status}`);
        }
      } catch (e) { errors.push(`Feedback: Error de conexión`); }

      if (errors.length > 0) setError(errors.join(' | '));
    } catch {
      setError('Error de conexión al cargar datos.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin, fetchData]);

  if (!isAdmin) {
    return <div className="text-center py-12 text-red-600">Acceso denegado</div>;
  }

  const totalClicks = Object.values(platformStats).reduce((a, b) => a + b, 0);
  const sortedPlatforms = Object.entries(platformStats).sort((a, b) => b[1] - a[1]);

  const platformColors: Record<string, string> = {
    uber: 'bg-gray-900', didi: 'bg-orange-500', indriver: 'bg-green-600',
    cabify: 'bg-purple-600', yango: 'bg-red-500',
  };
  const platformEmojis: Record<string, string> = {
    uber: '🚗', didi: '🟠', indriver: '🟢', cabify: '🟣', yango: '🔴',
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">📊 Panel de Administración</h1>
        <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
          🔄 Actualizar
        </button>
      </div>

      {error && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">{error}</div>}

      {/* Panel de configuración de tarifas */}
      <PricingConfig />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
          <p className="text-sm text-gray-500 mb-1">Total Usuarios</p>
          <p className="text-4xl font-bold text-blue-600">{loading ? '...' : users.length}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
          <p className="text-sm text-gray-500 mb-1">Verificados</p>
          <p className="text-4xl font-bold text-green-600">{loading ? '...' : users.filter(u => u.emailVerified).length}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-purple-500">
          <p className="text-sm text-gray-500 mb-1">Servicios Usados</p>
          <p className="text-4xl font-bold text-purple-600">{loading ? '...' : totalClicks}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
          <p className="text-sm text-gray-500 mb-1">Plataformas</p>
          <p className="text-4xl font-bold text-orange-600">{loading ? '...' : sortedPlatforms.length}</p>
        </div>
      </div>

      {/* Platform Usage */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">🚀 Uso de Plataformas</h2>
        {sortedPlatforms.length === 0 ? (
          <p className="text-gray-500 text-sm">Aún no hay datos de uso. Los datos se registran cuando los usuarios abren una app desde Fuigo.</p>
        ) : (
          <div className="space-y-4">
            {sortedPlatforms.map(([platform, count]) => {
              const pct = totalClicks > 0 ? (count / totalClicks) * 100 : 0;
              return (
                <div key={platform} className="flex items-center gap-4">
                  <span className="text-2xl w-8">{platformEmojis[platform] || '📱'}</span>
                  <span className="w-24 font-medium capitalize">{platform}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div className={`h-full rounded-full ${platformColors[platform] || 'bg-gray-500'} flex items-center justify-end pr-2`}
                      style={{ width: `${Math.max(pct, 5)}%` }}>
                      <span className="text-white text-xs font-bold">{count}</span>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 w-16 text-right">{pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">👥 Usuarios Registrados</h2>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : users.length === 0 ? (
          <p className="text-gray-500 text-sm">No se pudieron cargar los usuarios.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 font-semibold text-gray-600">Nombre</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-600">Email</th>
                  <th className="text-center py-3 px-2 font-semibold text-gray-600">Verificado</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-600">Registro</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-2">{u.name}</td>
                    <td className="py-3 px-2 text-gray-600">{u.email}</td>
                    <td className="py-3 px-2 text-center">{u.emailVerified ? '✅' : '❌'}</td>
                    <td className="py-3 px-2 text-gray-500">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('es-CO') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Platform Activity */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Actividad Reciente de Plataformas</h2>
        {clicks.length === 0 ? (
          <p className="text-gray-500 text-sm">No hay actividad registrada aún. Aparecerá cuando los usuarios abran apps desde Fuigo.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {clicks.slice(0, 50).map((click, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50">
                <span className="text-lg">{platformEmojis[click.platform] || '📱'}</span>
                <span className="font-medium capitalize">{click.platform}</span>
                <span className="text-gray-400 text-xs">por {click.userName || click.userEmail || 'Usuario'}</span>
                <span className="ml-auto text-xs text-gray-400">{new Date(click.createdAt).toLocaleString('es-CO')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feedback / Comments */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">💬 Comentarios de Usuarios</h2>
        {feedback.length === 0 ? (
          <p className="text-gray-500 text-sm">No hay comentarios aún. Los usuarios pueden dejar comentarios al usar los servicios.</p>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {feedback.map((fb, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{fb.userEmail}</span>
                  <span className="text-xs text-gray-400">{new Date(fb.createdAt).toLocaleString('es-CO')}</span>
                </div>
                {fb.platform !== 'general' && (
                  <span className="inline-block text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mb-1 capitalize">{fb.platform}</span>
                )}
                <p className="text-sm text-gray-600">{fb.comment}</p>
                {fb.rating > 0 && <p className="text-sm mt-1">{'⭐'.repeat(fb.rating)}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
